import type {
  QuoteOverviewParams,
  QuotePagesParams,
} from "@common/api/quoting/quote"
import {
  periodRange,
  periodSchema,
} from "@common/components/filters/period-model"
import { z } from "zod"
import { DISPOSITIONS } from "@/routes/_layout/quoting_/quotes/-components/disposition-chip"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  withNumber,
} from "./columns"
import { type Grouping, groupChoiceSchema, groupingSchema } from "./grouping"
import { outcomeSchema, withinOutcomes } from "./outcome"

export const TABS = ["overview", "all", "archived"] as const

const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

export const dispositionSchema = z.enum(DISPOSITIONS)

const directionSchema = z.enum(["asc", "desc"])

const layoutSchema = z.enum(["list"])

export type Direction = z.infer<typeof directionSchema>

export const quotesSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  status: z.array(dispositionSchema).optional().catch(undefined),
  outcome: z.array(outcomeSchema).optional().catch(undefined),
  customer: z.array(z.string()).optional().catch(undefined),
  aircraft: z.array(z.string()).optional().catch(undefined),
  model: z.array(z.string()).optional().catch(undefined),
  part: z.array(z.string()).optional().catch(undefined),
  department: z.array(z.string()).optional().catch(undefined),
  rep: z.array(z.string()).optional().catch(undefined),
  creator: z.array(z.string()).optional().catch(undefined),
  revision: z.array(z.number()).optional().catch(undefined),
  period: periodSchema.optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  layout: layoutSchema.optional().catch(undefined),
  group: groupChoiceSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
  months: z.number().int().min(1).max(12).optional().catch(undefined),
  end: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
})

export type QuotesSearch = z.infer<typeof quotesSearchSchema>

export type QuotesDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Column
  direction: Direction
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "all":
      return "All"
    case "overview":
      return "Overview"
    case "archived":
      return "Archived"
  }
}

export function quotePagesParams(search: QuotesSearch): QuotePagesParams {
  const display = displayOf(search)
  return {
    ...quoteFilters(search),
    sort: display.sort,
    direction: display.direction,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}

export function quoteOverviewParams(search: QuotesSearch): QuoteOverviewParams {
  return {
    customer_id: search.customer?.length ? search.customer : null,
    aircraft_id: search.aircraft?.length ? search.aircraft : null,
    aircraft_model: search.model?.length ? search.model : null,
    part_id: search.part?.length ? search.part : null,
    department_id: search.department?.length ? search.department : null,
    sales_rep_id: search.rep?.length ? search.rep : null,
    created_by_id: search.creator?.length ? search.creator : null,
    months: search.months,
    end: search.end ? `${search.end}-01` : null,
  }
}

function quoteFilters(search: QuotesSearch): QuotePagesParams {
  const range = periodRange(search.period, search.from, search.to)
  const dispositions = withinOutcomes(search.status, search.outcome)
  return {
    order: "updated",
    search: search.search || null,
    disposition: dispositions?.length
      ? dispositions
      : search.status?.length
        ? search.status
        : null,
    customer_id: search.customer?.length ? search.customer : null,
    aircraft_id: search.aircraft?.length ? search.aircraft : null,
    aircraft_model: search.model?.length ? search.model : null,
    part_id: search.part?.length ? search.part : null,
    department_id: search.department?.length ? search.department : null,
    sales_rep_id: search.rep?.length ? search.rep : null,
    created_by_id: search.creator?.length ? search.creator : null,
    revision: search.revision?.length ? search.revision : null,
    created_from: range.from,
    created_to: range.to,
    archived: search.tab === "archived",
  }
}

export function displayOf(search: QuotesSearch): QuotesDisplay {
  const group =
    search.layout === "list" || search.group === "none"
      ? undefined
      : (search.group ?? "status")
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    columns: search.columns ? withNumber(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "updated",
    direction: search.direction ?? (search.sort ? "asc" : "desc"),
  }
}
